import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import CategoryContent from './CategoryContent';

import colors from '../../../../styling/colors';
import compareProps from '../../../../utils/compareProps';
import HandOffLink from '../../../../utils/urls/HandOffLink';

const CategoryContainer = styled.li`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  margin: 0 20px;
  border-bottom: solid 4px transparent;

  .nav-open &.focused {
    border-bottom-color: ${colors('paloma')};
  }
`;

const CategoryLink = styled(HandOffLink)`
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1rem;
  letter-spacing: 0.15rem;
  text-transform: uppercase;

  display: block;
  width: 100%;
  position: relative;
  top: 0.125rem;
  text-align: center;
  text-decoration: none;
  cursor: pointer;

  &,
  &:link,
  &:visited {
    color: ${({ textColor }) => (textColor ? textColor : colors('lapel'))};
  }
`;

const SubTitle = styled.span`
  color: ${colors('gatsby')};
  font-size: 10px;
  letter-spacing: 1px;
`;

const CategoryDrawer = styled.div`
  width: 100%;
  position: absolute;
  top: 48px;
  left: 0;
  display: flex;
  overflow: hidden;
  background: #fff;
  z-index: 3;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 2px 2px 0px;
  visibility: hidden;

  &.focused {
    visibility: visible;
  }
`;

const CategoryHeader = memo(
  ({
    className = '',
    node,
    node: { id, title, href, color },
    isFocused,
    onFocused,
    onNodeClick,
  }) => {
    const colorValue = color && color.length ? color : undefined;

    return (
      <CategoryContainer
        className={`${className} ${isFocused ? 'focused' : ''}`}
      >
        <CategoryLink
          key={id}
          textColor={colorValue}
          href={href}
          preRedirects={[() => onNodeClick(node)]}
          onMouseEnter={() => onFocused(node)}
        >
          {title}
          {title === 'Flyer' && <SubTitle> New!</SubTitle>}
        </CategoryLink>
        <CategoryDrawer className={`nav-drawer ${isFocused ? 'focused' : ''}`}>
          <CategoryContent categoryData={node} onNodeClick={onNodeClick} />
        </CategoryDrawer>
      </CategoryContainer>
    );
  },
  // onFocused and onNodeClick change every render, so lets check the things that actually matter...
  (prevProps, nextProps) =>
    compareProps(prevProps, nextProps, ['node', 'isFocused']),
);

CategoryHeader.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    level: PropTypes.number,
    slug: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    color: PropTypes.string,
    childNodes: PropTypes.array,
  }).isRequired,
  onFocused: PropTypes.func.isRequired,
  onNodeClick: PropTypes.func.isRequired,
  isOpen: PropTypes.func.isRequired,
};

export default CategoryHeader;
